<script setup>
/* global axios */
import { onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TdfAPI from 'dashboard/api/tdfConfig';

const { t } = useI18n();

const isLoading = ref(false);
const isSubmitting = ref(false);

const form = ref({
  enabled: false,
  property_name: '',
  sync_motor_reserva: true,
  modules_config: {
    reception: true,
    reservations: true,
    direct_sales: true,
    clients: true,
    admin: true,
    other_routines: true,
  },
});

const fetchConfig = async () => {
  isLoading.value = true;
  try {
    const { data } = await TdfAPI.show();
    if (data && data.id) {
      form.value.enabled = data.enabled || false;
      form.value.property_name = data.property_name || '';
      form.value.sync_motor_reserva =
        data.sync_motor_reserva !== undefined ? data.sync_motor_reserva : true;
      if (data.modules_config && Object.keys(data.modules_config).length > 0) {
        form.value.modules_config = {
          ...form.value.modules_config,
          ...data.modules_config,
        };
      }
    }
  } catch {
    // Config not found, use defaults
  } finally {
    isLoading.value = false;
  }
};

const saveConfig = async () => {
  isSubmitting.value = true;
  try {
    await TdfAPI.update({ tdf_config: form.value });
    useAlert(t('TDF.CONFIG.API.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('TDF.CONFIG.API.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
  }
};

onBeforeMount(() => {
  fetchConfig();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div>
      <h3 class="text-base font-medium text-slate-800 dark:text-slate-100">
        {{ t('TDF.CONFIG.HEADER') }}
      </h3>
      <p class="text-sm text-slate-600 dark:text-slate-300">
        {{ t('TDF.CONFIG.DESCRIPTION') }}
      </p>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <span class="text-slate-500">{{ t('TDF.COMMON.LOADING') }}</span>
    </div>

    <form
      v-else
      class="flex max-w-2xl flex-col gap-6"
      @submit.prevent="saveConfig"
    >
      <!-- Enable TDF -->
      <div class="flex items-center gap-3">
        <input
          id="tdf-enabled"
          v-model="form.enabled"
          type="checkbox"
          class="size-4 rounded border-slate-300 text-blue-600"
        />
        <label
          for="tdf-enabled"
          class="text-sm font-medium text-slate-700 dark:text-slate-200"
        >
          {{ t('TDF.CONFIG.ENABLE_TDF') }}
        </label>
      </div>

      <!-- Property Name -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-slate-700 dark:text-slate-200">
          {{ t('TDF.CONFIG.PROPERTY_NAME') }}
        </label>
        <input
          v-model="form.property_name"
          type="text"
          :placeholder="t('TDF.CONFIG.PROPERTY_NAME_PLACEHOLDER')"
          class="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700"
        />
      </div>

      <!-- Motor de Reserva Sync -->
      <div
        class="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20"
      >
        <div class="flex items-center gap-3">
          <input
            id="sync-motor"
            v-model="form.sync_motor_reserva"
            type="checkbox"
            class="size-4 rounded border-slate-300 text-blue-600"
          />
          <div class="flex flex-col">
            <label
              for="sync-motor"
              class="text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              {{ t('TDF.CONFIG.SYNC_MOTOR_RESERVA') }}
            </label>
            <span class="text-xs text-slate-500 dark:text-slate-400">
              {{ t('TDF.CONFIG.SYNC_MOTOR_RESERVA_DESCRIPTION') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Active Modules -->
      <div class="flex flex-col gap-3">
        <span class="text-sm font-medium text-slate-700 dark:text-slate-200">
          {{ t('TDF.CONFIG.ACTIVE_MODULES') }}
        </span>
        <div class="grid grid-cols-2 gap-2">
          <label
            v-for="(value, key) in form.modules_config"
            :key="key"
            class="flex items-center gap-2 rounded-md border border-slate-200 p-3 dark:border-slate-600"
          >
            <input
              v-model="form.modules_config[key]"
              type="checkbox"
              class="size-4 rounded border-slate-300 text-blue-600"
            />
            <span class="text-sm text-slate-700 dark:text-slate-200">
              {{ t(`TDF.MODULES.${key.toUpperCase()}`) }}
            </span>
          </label>
        </div>
      </div>

      <!-- Submit -->
      <div>
        <button
          type="submit"
          class="rounded-md bg-blue-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-600 disabled:opacity-50"
          :disabled="isSubmitting"
        >
          {{ isSubmitting ? t('TDF.COMMON.SAVING') : t('TDF.CONFIG.SAVE') }}
        </button>
      </div>
    </form>
  </div>
</template>
